import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './button';
import { uploadAPI } from '../../services/api';
import { toast } from 'sonner';

export const ImageUpload = ({ 
  value, 
  onChange, 
  accept,  // Remove default, let getAcceptTypes handle it
  uploadType = 'image',
  label = 'Carica Immagine',
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  const getAcceptTypes = () => {
    if (accept) return accept;  // Use explicit accept if provided
    if (uploadType === 'document') return '.pdf,application/pdf';
    if (uploadType === 'cv') return '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    return 'image/*';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB for documents, 5MB for images)
    const maxSize = uploadType === 'image' ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File troppo grande (max ${uploadType === 'image' ? '5MB' : '10MB'})`);
      return;
    }

    setIsUploading(true);
    try {
      let response;
      if (uploadType === 'document') {
        response = await uploadAPI.document(file);
      } else if (uploadType === 'cv') {
        response = await uploadAPI.cv(file);
      } else {
        response = await uploadAPI.image(file);
      }
      
      const url = response.data.url;
      setPreview(url);
      onChange(url);
      toast.success('File caricato con successo!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del file', { duration: 8000 });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptTypes()}
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
      
      {preview ? (
        <div className="relative group">
          {uploadType === 'image' ? (
            <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border bg-muted">
              <img 
                src={preview} 
                alt="Preview" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleRemove}
                  data-testid="remove-image-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rimuovi
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm text-foreground truncate max-w-[200px]">
                  {preview.split('/').pop()}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRemove}
                className="text-destructive hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`
            relative w-full h-48 rounded-lg border-2 border-dashed border-border
            bg-muted/50 hover:bg-muted transition-colors cursor-pointer
            flex flex-col items-center justify-center space-y-3
            ${isUploading ? 'pointer-events-none opacity-60' : ''}
          `}
          data-testid="upload-dropzone"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
              <span className="text-sm text-muted-foreground">Caricamento in corso...</span>
            </>
          ) : (
            <>
              <Upload className="w-10 h-10 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{label}</span>
              <span className="text-xs text-muted-foreground">
                {uploadType === 'image' ? 'PNG, JPG, WEBP (max 5MB)' : 
                 uploadType === 'cv' ? 'PDF, DOC, DOCX (max 5MB)' : 'PDF (max 5MB)'}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
